import { Routes } from '@angular/router';
import { AdminLayoutComponent } from './shared/components/layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from './shared/components/layouts/auth-layout/auth-layout.component';
import { UserRoleGuard } from './shared/guards/user-role.guard';
import { HomeRedirectGuard } from './shared/guards/home-redirect.guard';
import { AccessDeniedComponent } from './views/access-denied/access-denied.component';
import { ComingSoonComponent } from './views/coming-soon/coming-soon/coming-soon.component';
import { ProfileComponent } from './views/profile/profile.component';
import { SupportComponent } from './views/support/support.component';
import { DriverLayoutComponent } from './shared/components/layouts/driver-layout/driver-layout.component';
import { DeliveryPickupDriverOrdersComponent } from './views/orders/orders-table/orders-table.component';
import { ViewOrdersComponent } from './views/orders/view-orders/view-orders.component';
import { ProgramComponent } from './views/programs/program/program.component';
import { LocumateComponent } from './views/locumate/locumate.component';
import { ChartComponent } from 'ng-apexcharts';
import { FlugerComponent } from './views/fluger/fluger.component';
import { ProfileType } from './shared/enums/profile-type.enum';
import { FacilityMaintenanceComponent } from './views/admin/facility-maintenance/facility-maintenance.component';
import { PerformerMaintenanceComponent } from './views/admin/performer-maintenance/performer-maintenance.component';
import { RadiologyLayoutComponent } from './shared/components/layouts/radiology-layout/radiology-layout.component';
import { FacilityAddDialogComponent } from './views/admin/facility-maintenance/dialogs/facility-add-dialog.component';
import { SuccessAgreementComponent } from './views/admin/facility-maintenance/success-agreement/success-agreement.component';


export const rootRouterConfig: Routes = [
  {
    path: '',
    canActivate: [HomeRedirectGuard],
    children: []
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: 'sessions',
        loadChildren: () =>
          import('./views/sessions/sessions.module').then(m => m.SessionsModule),
        data: { title: 'Session' },
      },
      // 🔴 MT-1084: the forced enrolment screen must render outside the portal shell.
      // While it was a child of AdminLayout the sidebar mounted for an account that
      // had not finished enrolling, and it showed live order counts. AuthLayout's
      // template is a bare <router-outlet>, so no shell is built at all.
      //
      // 🪤 The AuthLayout block has no canActivate -- the login screens must open
      // anonymously -- so UserRoleGuard is attached to this entry alone. That guard's
      // isLoggedIn() is a local token check, and its TOTP_ENROLMENT_ROUTE short-circuit
      // returns true at once, so it enforces only "a token must exist", with no server
      // round trip. Without it an anonymous visitor typing this URL would land on an
      // empty screen that 401s.
      {
        path: 'security',
        canActivate: [UserRoleGuard],
        loadChildren: () =>
          import('./views/security/security.module').then(m => m.SecurityModule),
      },
    ],
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
  },
  {
    path: '',
    component: AdminLayoutComponent,
    canActivate: [UserRoleGuard],
    canActivateChild: [UserRoleGuard],
    data: {
      role: [
        ProfileType.Admin,
        ProfileType.SuperAdmin,
        ProfileType.PortalUser,
        ProfileType.TelehealthPharmacy,
        ProfileType.ClinicalOperations,
        ProfileType.SuperBusinessUser,
        ProfileType.SuperBusinessUser,
        ProfileType.BusinessUser,
        ProfileType.Doctor,
        ProfileType.Nurse,
        ProfileType.NursePractitioner,
      ],
    },
    children: [
      {
        path: 'orders',
        loadChildren: () => import('./views/orders/orders.module').then(m => m.OrdersModule),
        data: { title: 'orders', breadcrumb: 'Orders' },
      },
      {
        path: 'stores',
        loadChildren: () => import('./views/stores/stores.module').then(m => m.StoresModule),
      },
      {
        path: 'clinician',
        loadChildren: () => import('./views/clinician/clinician.module').then(m => m.clinicianModule),
      },
      {
        path: 'search',
        loadChildren: () =>
          import('./views/search-view/search-view.module').then(m => m.SearchViewModule),
      },
      {
        path: 'admin',
        canActivateChild: [UserRoleGuard],
        data: { role: [ProfileType.Admin, ProfileType.SuperAdmin, ProfileType.ClinicalOperations] },
        loadChildren: () => import('./views/admin/admin.module').then(m => m.AdminModule),
      },
      {
        path: 'products',
        loadChildren: () =>
          import('./views/products/products.module').then(m => m.ProductsModule),
      },
      {
        path: 'users',
        data: {
          role: [
            ProfileType.Admin,
            ProfileType.SuperAdmin,
            ProfileType.BusinessUser,
            ProfileType.SuperBusinessUser,
          ],
        },
        loadChildren: () =>
          import('./views/users/customers.module').then(m => m.CustomersModule),
      },
      {
        path: 'telehealth',
        loadChildren: () =>
          import('./views/telehealth/telehealth.module').then(m => m.TelehealthModule),
      },
      {
        path: 'resources',
        loadChildren: () => import('./views/programs/programs.module').then(m => m.ProgramsModule),
      },
      // {
      //   path: 'invite',
      //   component: CustomersComponent,
      // },
      {
        path: 'reports',
        // component: ReportsComponent,
        // canActivate: [UserRoleGuard],
        loadChildren: () =>
          import('./views/reports/reports.module').then(m => m.ReportsModule),
      },
      {
        path: 'billing',
        canActivateChild: [UserRoleGuard],
        data: { role: [ProfileType.Admin, ProfileType.SuperAdmin] },
        loadChildren: () =>
          import('./views/billing/billing.module').then(m => m.BillingModule),
      },
      {
        path: 'patient-programs',
        canActivateChild: [UserRoleGuard],
        data: { role: [ProfileType.Admin, ProfileType.SuperAdmin] },
        loadChildren: () =>
          import('./views/patient-programs/patient-programs.module').then(m => m.PatientProgramsModule),
      },
      {
        path: 'dashboard',
        // component: ChartComponent,
        loadChildren: () =>
          import('./views/dashboard-2/dashboard-2.module').then(m => m.Dashboard2Module),
      },
      {
        path: 'locumate',
        component: LocumateComponent,
      },
      // {
      //   path: 'my-programs',
      //   loadChildren: () => import('./views/programs/programs-routing.module').then(m => m.ProgramsRoutingModule)
      // },
      {
        path: 'profile',
        component: ProfileComponent,
      },
      {
        path: 'coming-soon',
        component: ComingSoonComponent,
      },
      {
        path: 'support',
        component: SupportComponent,
      },
      {
        path: 'fluger',
        component: FlugerComponent
      }
    ]
  },
  {
    path: 'driver',
    redirectTo: 'driver/delivery_pickup',
    pathMatch: 'full',
  },
  {
    path: 'driver',
    component: DriverLayoutComponent,
    canActivate: [UserRoleGuard],
    data: { role: [ProfileType.Driver] },
    children: [
      {
        path: 'delivery_pickup',
        component: DeliveryPickupDriverOrdersComponent,
        data: { title: 'DeliveyPickup', breadcrumb: 'Delivery & Pickup' },
      },
      {
        path: 'vieworders/:id',
        component: ViewOrdersComponent,
        data: { title: 'view', breadcrumb: 'view' },
      },
      {
        path: 'coming-soon',
        component: ComingSoonComponent
      },
      {
        path: 'vieworders/:id',
        component: ViewOrdersComponent,
        data: { title: "view", breadcrumb: 'view' },
      },
    ],
  },
  {
    path: 'radiology',
    component: RadiologyLayoutComponent,
    canActivate: [UserRoleGuard],
    data: { role: [ProfileType.RadiologyUser] },
    children: [
      {
        path: 'facility-management',
        component: FacilityMaintenanceComponent,
        data: { title: 'Facility Management', breadcrumb: 'Facility Management' }
      },
      {
        path: 'performer-maintenance',
        component: PerformerMaintenanceComponent,
        data: { title: 'Performer Maintenance', breadcrumb: 'Performer Maintenance' }
      },
    ],
  },
  {
    path: 'facility-management-agreement/success',
    component: SuccessAgreementComponent
  },
  {
    path: 'facility-management-agreement/:hash',
    component: FacilityAddDialogComponent,
    data: { isStandalonePage: false, isStandalonePageAgreement: true, isEdit: false }
  },
  {
    path: 'facility-management-add',
    component: FacilityAddDialogComponent,
    data: { isStandalonePage: true, isEdit: false }
  },
  {
    path: '**',
    redirectTo: 'sessions/404',
  },
];
